import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import { useNavigate, useParams } from 'react-router-dom'
import { Audit } from '../../types/auditTypes'
import { getAuditbyId, updateAudit } from '../../redux/audits/auditActions'
import UseAlert from '../../components/common/alert'
import { useTranslation } from 'react-i18next'
import {
  FormAside,
  FormField,
  FormFoot,
  FormGrid,
  FormLayout,
  FormPageHead,
  FormSection,
  FormShell,
} from '../../components/common/form'
import '../../i18n'

export default function AuditUpdate() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const { id } = useParams<{ id: string }>()

  const [audit, setAudit] = useState<Audit>({
    AuditId: '',
    AuditName: '',
    Description: '',
    MissionId: '',
  })

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  const auditsList = useSelector((state: RootState) => state.audit.audit)
  const loading = useSelector((state: RootState) => state.audit.loading)
  const error = useSelector((state: RootState) => state.audit.error)

  useEffect(() => {
    if (id) dispatch(getAuditbyId(id))
  }, [dispatch, id])

  useEffect(() => {
    if (auditsList !== null) {
      setAudit({
        _id: auditsList._id,
        AuditId: auditsList.AuditId,
        AuditName: auditsList.AuditName,
        Description: auditsList.Description,
        MissionId: auditsList.MissionId,
      })
    }
  }, [auditsList])

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false })
    if (alert.severity === 'success') navigate('/audits')
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setAudit((prev) => ({ ...prev, [name]: value }))
  }

  const submitLabel = t('Update')

  const handleUpdateAudit = async () => {
    try {
      if (audit.AuditName && audit.Description && audit._id) {
        await dispatch(
          updateAudit({
            updatedAudit: audit,
            _id: audit._id,
          })
        )
        setAlert({
          open: true,
          message: t('alerts.auditUpdate.success'),
          severity: 'success',
        })
      } else {
        setAlert({
          open: true,
          message: t('alerts.auditUpdate.emptyFields'),
          severity: 'error',
        })
      }
    } catch (error: any) {
      setAlert({
        open: true,
        message: t('alerts.auditUpdate.error'),
        severity: 'error',
      })
    }
  }

  if (loading && !audit._id) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress sx={{ color: '#0d9b87' }} />
      </Box>
    )
  }

  if (error && !audit._id) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  return (
    <div className="page-content">
      <FormPageHead
        eyebrow={t('entityForms.audit.updateEyebrow', {
          defaultValue: 'Sources · Audits',
        })}
        title={t('Update Audit')}
        subtitle={t('entityForms.audit.updateSubtitle', {
          defaultValue: 'Update the audit name and description used in reports.',
        })}
        backLabel={t('entityForms.audit.backToList', {
          defaultValue: 'Back to audits',
        })}
        onBack={() => navigate('/audits')}
      />

      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />

      <FormLayout
        main={
          <FormShell>
            <FormSection
              title={t('entityForms.audit.sectionTitle', {
                defaultValue: 'Audit details',
              })}
              description={t('entityForms.audit.sectionDesc', {
                defaultValue:
                  'These fields identify the audit when it appears as a task source.',
              })}
            >
              <FormGrid cols={1}>
                <FormField label={t('Audit Name')} required>
                  <input
                    className="incyia-form-inp"
                    name="AuditName"
                    value={audit.AuditName}
                    onChange={handleInputChange}
                    required
                  />
                </FormField>
                <FormField label={t('Description')} required>
                  <textarea
                    className="incyia-form-inp"
                    name="Description"
                    value={audit.Description}
                    onChange={handleInputChange as any}
                    rows={4}
                    required
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormFoot
              note={t('actionCreate.aside.requiredMark', {
                defaultValue: '* Required',
              })}
              cancelLabel={t('Cancel')}
              submitLabel={submitLabel}
              onCancel={() => navigate('/audits')}
              onSubmit={handleUpdateAudit}
            />
          </FormShell>
        }
        aside={
          <FormAside
            title={t('actionCreate.aside.inThisForm', {
              defaultValue: 'In this form',
            })}
            tipTitle={t('actionCreate.aside.goodToKnow', {
              defaultValue: 'Good to know',
            })}
            tip={t('entityForms.audit.tip', {
              defaultValue:
                'Mission linkage is managed from the audit’s mission context and is not edited here.',
            })}
            steps={[
              {
                title: t('entityForms.audit.sectionTitle', {
                  defaultValue: 'Audit details',
                }),
                fieldCount: 2,
              },
            ]}
            requiredCount={2}
            submitLabel={submitLabel}
            fieldsLabel={(n) =>
              t(
                n === 1
                  ? 'actionCreate.aside.fields_one'
                  : 'actionCreate.aside.fields_other',
                { count: n, defaultValue: `${n} fields` }
              )
            }
            requiredNote={(count, submit) =>
              t('actionCreate.aside.requiredNote', {
                count,
                submit,
                defaultValue: `${count} required before you can ${submit}.`,
              })
            }
          />
        }
      />
    </div>
  )
}
